import { trackUser } from '@api/tracking/client';
import { useRagnar } from '@mentimeter/ragnar-react';
import { IdentitiesList } from '@mentimeter/slides-presentation-ui/identified-responses/list';
import { IdentitiesListModal } from '@mentimeter/slides-presentation-ui/identified-responses/modal';
import type { MultipleChoicePieChartRenderChoiceLegend } from '@mentimeter/visualizations-react/types/render-options-by-slide-type/multiple-choice';
import { FullSizeContainer } from '@mentimeter/visualizations-react/utils/full-size-container';
import { PieVisualization } from '@mentimeter/visualizations-react/visualizations/pie';
import type {
  Choice,
  ChoicesVisualizationProps,
  OpenedIdentifiedResponsesListEvent,
} from '../types';
import {
  getIdentitiesWithNameFallback,
  getParticipantsWithNameFallback,
} from '../utils/getIdentitiesWithNameFallback';
import { useIdentifiedResultsListState } from './useIdentifiedResultsListState';

interface PieDataHandlerProps extends ChoicesVisualizationProps {
  visualizationType: 'pie' | 'donut';
  renderChoiceLegend?: MultipleChoicePieChartRenderChoiceLegend;
}

export function PieDataHandler({
  showCorrect,
  results,
  choices,
  shouldShowPercent,
  identifiedResults,
  shouldHideResults,
  visualizationType,
  shouldAnimate,
  renderChoiceLegend,
  renderChoiceLabel,
}: PieDataHandlerProps) {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
      fonts: { body },
    },
  } = useRagnar();
  const { results: currentResults } = results;

  const visualizationData = currentResults.map((result) =>
    shouldHideResults
      ? {
          id: result.id,
          score: 0,
        }
      : {
          id: result.id,
          score: result.score.reduce((a, b) => a + b, 0),
        },
  );

  const colors = fillColors.length > 0 ? fillColors : [textColor];

  const handleShowIdentitiesList = (choiceId: Choice['id']) => {
    trackUser({
      event: 'Opened identified responses list',
      properties: {
        'slide type': 'choices',
        visualization: visualizationType,
        placement: 'Visualization',
        context: 'Presentation view',
      },
    } satisfies OpenedIdentifiedResponsesListEvent);
    setIdentitiesListChoiceId(choiceId);
  };

  const {
    setIdentitiesListChoiceId,
    identityListChoice,
    identityListChoiceIndex,
    activeChoiceIdentities,
  } = useIdentifiedResultsListState({
    choices,
    identifiedResults: identifiedResults ?? [],
    shouldHideResults,
  });

  const shouldShowIdentitiesList = Boolean(
    identityListChoice !== undefined &&
      activeChoiceIdentities?.participantIdentities?.length &&
      activeChoiceIdentities.participantIdentities.length > 0 &&
      visualizationData.length > 0,
  );

  const totalScore =
    visualizationData
      .map(({ score }) => score)
      .reduce((acc, cur) => acc + cur, 0) || 1;

  return (
    <>
      <FullSizeContainer>
        {(width, height) => (
          <PieVisualization
            colors={colors as [string, ...[string]]}
            textColor={textColor}
            data={visualizationData}
            staticOptions={choices}
            width={width}
            height={height}
            shouldShowCorrect={showCorrect}
            shouldShowScorePercentage={
              shouldShowPercent ? { percentOf: totalScore } : false
            }
            isDonut={visualizationType === 'donut'}
            disableAnimations={!shouldAnimate}
            identities={getIdentitiesWithNameFallback(identifiedResults) ?? []}
            onClickOption={
              identifiedResults === null ? undefined : handleShowIdentitiesList
            }
            fontFamily={body}
            renderChoiceLegend={renderChoiceLegend}
            renderChoiceLabel={renderChoiceLabel}
          />
        )}
      </FullSizeContainer>
      {shouldShowIdentitiesList && (
        <IdentitiesListModal onDismiss={() => setIdentitiesListChoiceId(null)}>
          <IdentitiesList
            identities={
              getParticipantsWithNameFallback(
                activeChoiceIdentities?.participantIdentities,
              ) ?? []
            }
            color={
              fillColors[identityListChoiceIndex % fillColors.length] ?? '#000'
            }
            title={identityListChoice?.label ?? 'Choice not found'}
            totalResponses={activeChoiceIdentities?.responseCount ?? 0}
          />
        </IdentitiesListModal>
      )}
    </>
  );
}
